/* ── Tema för 3D-visaren ────────────────────────────
 * Visaren ritar i WebGL och kan inte läsa CSS-variabler själv. Här läses KanPlans
 * tokens från canvasens element och lämnas till motorn (engine.applyTheme):
 * bakgrund (--kp-inset), rutnät (--kp-blue-200) och markering/hover (--kp-accent).
 * Körs vid start och varje gång ljust/mörkt läge växlar (klassen "dark" på html eller body).
 */

function lasToken(el, name) {
  try {
    return el ? getComputedStyle(el).getPropertyValue(name).trim() : '';
  } catch {
    return '';
  }
}

// Tokens ärvs från html (index.css), så canvasen hittar dem även utan .bim-module-förälder
// (t.ex. i dokumentmodulens förhandsvisning). Ger elementet inget värde, t.ex. om det inte
// sitter i dokumentet än, läses de direkt från html och body.
function token(el, name) {
  const doc = typeof document !== 'undefined' ? document : null;
  return lasToken(el, name) || lasToken(doc?.documentElement, name) || lasToken(doc?.body, name);
}

// Bara hex- och rgb()-värden förs över; annat (t.ex. rgba-tokens) ger reservfärgen.
function farg(varde, reserv) {
  return /^#[0-9a-f]{3,8}$/i.test(varde) || /^rgb\(/i.test(varde) ? varde : reserv;
}

/** Blandar en hex-/rgb()-färg mot vitt (andel 0–1). Returnerar rgb(). */
function motVitt(css, andel) {
  let r; let g; let b;
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(css);
  if (hex) {
    const h = hex[1].length === 3 ? hex[1].split('').map((c) => c + c).join('') : hex[1];
    [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
  } else {
    const m = /^rgb\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/i.exec(css);
    if (!m) return css;
    [r, g, b] = [m[1], m[2], m[3]].map(Number);
  }
  const mix = (c) => Math.round(c + (255 - c) * andel);
  return `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`;
}

/**
 * Läser scenens färger från `el` (reserv: html/body, sedan fasta värden).
 * @param {HTMLElement} el canvasens element
 * @returns {{ background: string, grid: string, select: string, hover: string }} CSS-färger
 */
export function readViewerColors(el) {
  const accent = farg(token(el, '--kp-accent'), 'rgb(59, 130, 246)');
  return {
    background: farg(token(el, '--kp-inset'), 'rgb(240, 242, 245)'),
    grid: farg(token(el, '--kp-blue-200'), 'rgb(203, 213, 225)'),
    select: accent,
    // Hover-förval: accent blandad 45 % mot vitt.
    hover: motVitt(accent, 0.45),
  };
}

/**
 * Anropar `onChange` när klassen på html eller body ändras (temaväxling).
 * @returns {() => void} avregistrering
 */
export function observeTheme(onChange) {
  if (typeof MutationObserver === 'undefined' || typeof document === 'undefined') return () => {};
  const obs = new MutationObserver(() => onChange());
  obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  if (document.body) obs.observe(document.body, { attributes: true, attributeFilter: ['class'] });
  return () => obs.disconnect();
}
